<template>
  <div v-if="state.banners && state.banners.length > 0" class="footer-banners">
    <Grid class="footer-banners-grid">
      <ul class="footer-banners-wrapper">
        <li v-for="banner in state.banners" :key="banner.title">
          <NuxtLink
            :to="banner.url"
            :external="isErrorLayout"
            class="footer-banner-link"
            style="position: relative"
            @click="handleClick($event, banner.url, banner.title)"
          >
            <DynamicBackgroundImage
              class="footer-banner"
              :image="getBannerImage(banner)"
              :renditions="renditions"
            >
              <div class="footer-banner-icon-wrapper">
                <Icon :name="banner.icon" class="footer-banner-icon" role="presentation" />
              </div>

              <Heading :level="2" class="footer-banner-content">
                <span class="footer-banner-prefix">{{ banner.prefix }}&nbsp;</span
                ><span class="footer-banner-title">{{ banner.title }}</span>
              </Heading>
            </DynamicBackgroundImage>
          </NuxtLink>
        </li>
      </ul>
    </Grid>
  </div>
</template>

<script setup lang="ts">
import useGlobalConfigStore from '~/stores/global';
import type { ICmsFooterBanners } from '~~/types/_cms-types';
import type { IImageRendition } from '~~/types/local_types';

const globalConfigStore = useGlobalConfigStore();
// 1. Route nodig om huidige URL te vergelijken
const route = useRoute();

interface IState {
  banners?: ICmsFooterBanners[];
}

const state = reactive<IState>({});

globalConfigStore.$subscribe(
  () => {
    state.banners = globalConfigStore.footerBanners;
  },
  { immediate: true },
);

const renditions: IImageRendition[] = [{ breakpoint: 'sm', size: 480, aspectRatio: '4x3' }];

const getBannerImage = (banner: ICmsFooterBanners) => {
  return {
    ...banner.image,
    description: (banner.image?.description && banner.image?.description !== '') ? banner.image.description : banner.title,
  };
};

const normalizePath = (path: string | undefined) => {
  if (!path) return '';
  return (
    path
      .replace(/^(https?:\/\/[^/]+)?/, '') // Verwijder domein
      .split('?')[0] ??
    '' // Verwijder query (en val terug op '' als het misgaat)
      .split('#')[0] ??
    ('' // Verwijder hash (en val terug op '' als het misgaat)
      .replace(/\/+$/, '') ||
      '/') // Verwijder trailing slash
  );
};

const handleClick = (event: Event, linkUrl: string | undefined, linkText: string | undefined) => {
  gtmTrackEvent({
    linkAnalyticsTag: 'footer-banners',
    linkText,
    linkUrl,
  });

  if (!linkUrl) return;

  const currentPath = normalizePath(route.path);
  const targetPath = normalizePath(linkUrl);

  if (currentPath === targetPath) {
    event.preventDefault();

    // Scroll naar boven
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

defineProps({
  isErrorLayout: {
    type: Boolean,
    default: false,
  },
});
</script>

<style scoped lang="postcss">
.footer-banner-link {
  display: block;
  position: relative;

  &.router-link-active,
  &.router-link-exact-active {
    pointer-events: auto !important;
    cursor: pointer !important;
  }
  /* ----------------------------- */

  @add-mixin focus light;

  &:hover {
    .footer-banner-title {
      color: hsl(var(--color-primary-200));
    }
  }

  &:active {
    .footer-banner-title {
      color: hsl(var(--color-primary-300));
    }
  }
}

.footer-banners {
  width: 100%;
  display: inline-block;
  position: relative;
  &::before {
    position: absolute;
    content: '';
    background: linear-gradient(180deg, rgba(241, 242, 249, 0) 0%, #e5e7f6 100%);
    bottom: 0;
    width: 100%;
    height: 100%;
  }
}
.footer-banners-grid {
  --footer-banners-margin-top: var(--unit-24);
  --footer-banners-margin-bottom: var(--unit-24);
  position: relative;
  margin-top: var(--footer-banners-margin-top);
  margin-bottom: var(--footer-banners-margin-bottom);
  @media (--lg-viewport) {
    --footer-banners-margin-top: var(--unit-32);
    --footer-banners-margin-bottom: var(--unit-48);
  }
}
.footer-banners-wrapper {
  --footer-banners-wrapper-row-gap: calc(var(--base-spacing) * 2);
  --footer-banners-wrapper-col-gap: calc(var(--base-spacing) * 3);
  display: grid;
  grid-template-columns: minmax(125px, 1fr) minmax(125px, 1fr);
  row-gap: var(--footer-banners-wrapper-row-gap);
  column-gap: var(--footer-banners-wrapper-col-gap);
  @media (--md-viewport) {
    --footer-banners-wrapper-col-gap: calc(var(--base-spacing) * 4);
  }
  @media (--lg-viewport) {
    --footer-banners-wrapper-col-gap: calc(var(--base-spacing) * 6);
    grid-template-columns: minmax(125px, 1fr) minmax(125px, 1fr) minmax(125px, 1fr) minmax(
        125px,
        1fr
      );
  }
}
.footer-banner {
  --footer-banners-padding: calc(var(--base-spacing) * 4);
  --footer-banners-height: 163px;
  position: relative;
  color: hsl(var(--color-neutral-white));
  display: flex;
  flex-direction: column;
  background: var(--effect-gradient-primary-4);
  height: var(--footer-banners-height);
  width: 100%;
  padding: var(--footer-banners-padding);
  justify-content: space-between;
  background-size: cover;
  @media (--lg-viewport) {
    --footer-banners-padding: calc(var(--base-spacing) * 5);
    --footer-banners-height: 178px;
  }
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    bottom: 0;
    background: var(--effect-gradient-mask);
    z-index: 0;
  }
}
.footer-banner-icon-wrapper {
  --footer-banners-icon-wrapper-size: var(--unit-44);
  position: relative;
  width: var(--footer-banners-icon-wrapper-size);
  height: var(--footer-banners-icon-wrapper-size);
  border-radius: 50%;
  background: var(--effect-gradient-brand);
  box-shadow:
    0px 2.526px 5.053px 0px rgba(0, 0, 0, 0.12),
    0px 0px 30.316px 0px rgba(0, 0, 0, 0.25),
    0px -1.263px 0px 0px rgba(0, 0, 0, 0.12) inset;
  align-self: flex-start;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (--lg-viewport) {
    --footer-banners-icon-wrapper-size: 48px;
  }
}
.footer-banner-icon {
  font-size: var(--unit-24);
}
.footer-banner-content {
  --footer-banners-content-gap: calc(var(--base-spacing) * 0.5);
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--footer-banners-content-gap);
  @media (--lg-viewport) {
    --footer-banners-content-gap: var(--base-spacing);
  }
}
.footer-banner-prefix {
  color: hsl(var(--color-primary-200));
  @mixin heading-2xs {
  }
}
.footer-banner-title {
  @mixin heading-md-bold {
  }
  transition: color 250ms ease;
}
</style>
